/* RunIt overlay coordination.
 *
 * The storefront has several pieces of floating UI that can appear at the same
 * time (Shopify cookie consent, the wishlist sheet/toast, the cart drawer,
 * quick-add, the promo popup, hCaptcha, and the blackjack popup + its floating
 * "PLAY FOR A DISCOUNT" pill). Instead of each one fighting the others with
 * z-index, this file owns ONE state object and publishes one verdict:
 *
 *     blackjackVisible = blackjackAllowedByPage AND noHigherPriorityOverlay
 *
 * Priority, highest first:
 *   1. cookie / privacy consent          (consent)
 *   2. wishlist sheet, toast, wishlist list
 *   3. cart drawer
 *   4. quick-add / variant-selection modals
 *   5. other blocking theme modals       (promo popup, product modal, challenges)
 *   6. the navigation drawer and the size chart
 *   7. temporary notifications
 *   8. the blackjack floating CTA        <- lowest, and it HIDES, never stacks
 *
 * Nothing here resets, closes or stores anything belonging to the blackjack
 * game: the game asks this file whether it may SHOW, and hides its own trigger.
 * Suspend/resume of an already-open popup lives in runit-blackjack.js.
 *
 * Signals are Shopify-native where they exist — Shopify.customerPrivacy
 * (shouldShowBanner / visitorConsentCollected) for consent — with a single
 * MutationObserver over the document as the fallback for element visibility.
 * There is no polling loop.
 */
(function () {
  'use strict';

  /* One coordinator per page. This is loaded from the header group so it is
     first in document order, but a second copy arriving from a section reload
     must not spawn a second observer with a second opinion. */
  if (window.RunItOverlays) return;

  /* Templates blackjack is allowed on, by Shopify page type. An informational
     page, a policy, /cart, an account page or a 404 is simply not in the list,
     so a NEW page type is off by default rather than on. The Liquid side gates
     the markup and the assets on the same list (layout/theme.liquid). */
  var ALLOWED_PAGE_TYPES = ['index', 'collection', 'list-collections', 'search'];

  /* How long to leave the CTA down after a blocking surface closes, so the
     closing animation finishes first. Consent gets the longer hold the brief
     asks for; the others are a transition's length. */
  var RESTORE_DELAY = { consent: 1500, wishlist: 400, cart: 400, quickAdd: 400,
                        modal: 400, notification: 400 };
  var CONSENT_UNKNOWN_TIMEOUT = 9000;

  var body = document.body;
  var pageType = (body && body.getAttribute('data-page-type')) || '';
  var allowed = ALLOWED_PAGE_TYPES.indexOf(pageType) !== -1;

  /* Each surface: the selectors that mean "this is up", and how to tell. The
     consent surface also knows the Shopify API, and the drawers are detected by
     computed visibility because this theme toggles no class on them. */
  var SURFACES = [
    { key: 'consent', delay: 'consent', sels: ['[data-runit-cookie-banner]', '#shopify-privacy-banner'] },
    { key: 'wishlist', delay: 'wishlist',
      sels: ['[data-runit-wishlist-sheet]', '[data-runit-wishlist-panel]:not([hidden])'] },
    { key: 'cart', delay: 'cart', sels: ['cart-drawer', '#CartDrawer'] },
    { key: 'quickAdd', delay: 'quickAdd', sels: ['quick-add-modal', '.quick-add-modal'] },
    { key: 'modal', delay: 'modal',
      sels: ['.sign-up-popup-modal.popup-modal--active', 'product-modal[open]',
             '.modal--active', 'shopify-challenge', '.shopify-challenge__container'] },
    /* The two full-screen surfaces the CTA was landing on top of on a phone: the
       navigation drawer and the size chart. Both are this theme's own markup, and
       both are detected the same way as everything else here — by whether they
       are painted — because neither reports itself.

       The drawer is matched on `details.menu-opening` rather than on the drawer
       itself: the panel it slides in keeps `visibility: hidden` while a child is
       painted, so the element that IS on screen is the closed-looking one. The
       size chart is a real `dialog[open]`, which also covers the theme's other
       modal dialogs. */
    { key: 'menu', delay: 'modal',
      sels: ['details.menu-opening', 'header-drawer[open]'] },
    { key: 'dialog', delay: 'modal', sels: ['dialog[open]'] },
    { key: 'concierge', delay: 'modal', sels: ['[data-runit-concierge] .rl-chat__panel:not([hidden])'] },
    { key: 'notification', delay: 'notification', sels: ['[data-runit-wishlist-toast]'] }
  ];

  var PRIORITY = SURFACES.map(function (s) { return s.key; });

  var state = { consent: 'unknown' };
  for (var p = 0; p < PRIORITY.length; p++) {
    if (PRIORITY[p] !== 'consent') state[PRIORITY[p]] = false;
  }

  /* A surface counts as up only if it is actually painted: this theme leaves
     closed overlays in the DOM with visibility: hidden or opacity: 0. */
  function painted(el) {
    if (!el) return false;
    if (el.hasAttribute('hidden') && el.getAttribute('hidden') !== 'false') return false;
    var cs = window.getComputedStyle(el);
    if (!cs || cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function anyPainted(sels) {
    for (var i = 0; i < sels.length; i++) {
      var nodes = document.querySelectorAll(sels[i]);
      for (var n = 0; n < nodes.length; n++) {
        if (painted(nodes[n])) return true;
      }
    }
    return false;
  }

  /* ---------------------------------------------------------------- consent */

  /* `ready` is the difference between "the shopper has no cookie question" and
     "we have not asked Shopify yet". The store's own consent script only shows
     the banner on window.load, so between DOMContentLoaded and that moment a
     banner that IS coming looks exactly like no banner at all — which is how a
     CTA flashes up behind a first-visit cookie screen. Nothing is reported as
     closed until the question has been answered, one way or the other. */
  var consent = { known: false, ready: false, bannerSeen: false, collected: false, api: null, timer: null };

  function consentIsOpen() {
    if (anyPainted(['[data-runit-cookie-banner]', '#shopify-privacy-banner'])) {
      consent.bannerSeen = true;
      return true;
    }
    /* Shopify's own answer, for the moment between "should show" and the banner
       being painted — and for themes that render it later than we look. */
    if (!consent.collected && consent.api && typeof consent.api.shouldShowBanner === 'function') {
      try {
        if (consent.api.shouldShowBanner()) return true;
      } catch (e) { /* fall through to the element check only */ }
    }
    return false;
  }

  function readConsent() {
    if (consentIsOpen()) return 'open';
    /* It was up and it is gone: the shopper has answered. */
    if (consent.bannerSeen || consent.collected) return 'closed';
    return consent.ready ? 'closed' : 'unknown';
  }

  function bindConsentApi() {
    var shopify = window.Shopify;
    if (!shopify || typeof shopify.loadFeatures !== 'function') return;
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {
        consent.api = window.Shopify && window.Shopify.customerPrivacy;
        consent.known = true;
        /* With the API in hand, "no banner is due" is a real verdict. "A banner
           is due" is not resolved yet — the banner itself, or the store's own
           script announcing it is done, resolves that. */
        try {
          if (!consent.api || typeof consent.api.shouldShowBanner !== 'function' ||
              !consent.api.shouldShowBanner()) {
            consent.ready = true;
          }
        } catch (e) {
          consent.ready = true;
        }
        refresh();
      });
    } catch (e) {
      consent.known = true;
      consent.ready = true;
    }
  }

  /* Never leave the CTA hostage to an API that did not load. */
  consent.timer = window.setTimeout(function () {
    if (!consent.known || !consent.ready) {
      consent.known = true;
      consent.ready = true;
      refresh();
    }
  }, CONSENT_UNKNOWN_TIMEOUT);

  document.addEventListener('visitorConsentCollected', function () {
    consent.collected = true;
    consent.known = true;
    consent.ready = true;
    refresh();
  });

  /* The store's own consent script (assets/runit-consent.js) says when its
     banner has been dealt with — shown, refused, accepted or customised. */
  document.addEventListener('runit:consent-ready', function () {
    consent.known = true;
    consent.ready = true;
    refresh();
  });

  /* ------------------------------------------------------------- the verdict */

  function readSurfaces() {
    for (var i = 0; i < SURFACES.length; i++) {
      var surface = SURFACES[i];
      if (surface.key === 'consent') {
        state.consent = readConsent();
      } else {
        state[surface.key] = anyPainted(surface.sels);
      }
    }
  }

  function blockingKeys() {
    var keys = [];
    for (var i = 0; i < PRIORITY.length; i++) {
      var key = PRIORITY[i];
      if (key === 'consent') {
        /* Unknown counts as blocking: the CTA (and the auto-open) waits for a
           verdict rather than flashing up behind a consent screen. */
        if (state.consent === 'open' || state.consent === 'unknown') keys.push(key);
      } else if (state[key]) {
        keys.push(key);
      }
    }
    return keys;
  }

  /* What to compare against, so a mutation we caused ourselves cannot spin the
     observer: the live surfaces, plus whether a restore is already pending. */
  function signature(live) {
    return live.join(',') + '|hold=' + (restoreTimer ? heldKeys.join(',') : '-') +
      '|allowed=' + allowed;
  }

  var lastSignature = null;
  var publishedBlocked = false;
  var restoreTimer = null;
  /* Surfaces whose restore is being held back, and what was up at the previous
     read — a closing animation is over before we ever look, so "what was up a
     moment ago" is the only reliable way to know whose transition to wait out. */
  var heldKeys = [];
  var liveKeys = [];
  var subscribers = [];

  function publish() {
    var live = blockingKeys();
    /* While a restore is being held back, the surface that just closed still
       counts as owning the screen. Without this the hold would be invisible to
       every consumer — the CTA would pop back the instant the modal went, and
       the 1-2s the brief asks for would exist only on paper. */
    var holding = !live.length && !!restoreTimer;
    var keys = holding ? heldKeys.slice() : live;
    var blocked = keys.length > 0;
    publishedBlocked = blocked;
    var detail = { blocking: blocked, holding: holding, keys: keys, state: state,
                   allowed: allowed, blackjackVisible: allowed && !blocked };
    document.documentElement.setAttribute('data-rl-overlays', blocked ? keys[0] : 'clear');
    for (var i = 0; i < subscribers.length; i++) {
      try { subscribers[i](detail); } catch (e) { /* a subscriber must not break the rest */ }
    }
    window.dispatchEvent(new CustomEvent('runit:overlays', { detail: detail }));
    return detail;
  }

  function release() {
    restoreTimer = null;
    heldKeys = [];
    publish();
  }

  /* A surface that announces it is closing starts or extends the hold itself.
     It is the precise signal — the element is often already gone by the time we
     look, and then there is no transition left to notice. */
  function hold(keys) {
    for (var i = 0; i < keys.length; i++) {
      if (heldKeys.indexOf(keys[i]) === -1) heldKeys.push(keys[i]);
    }
    readSurfaces();
    var now = blockingKeys();
    if (now.length) {
      /* Something else took the screen: keep holding, restart the timer later. */
      if (restoreTimer) {
        window.clearTimeout(restoreTimer);
        restoreTimer = null;
      }
      heldKeys = now.slice();
      liveKeys = now.slice();
      publish();
      return;
    }
    arm(heldKeys.length ? heldKeys : keys);
    publish();
  }

  /* Start (or restart) the wait before the CTA may come back. */
  function arm(keys) {
    if (restoreTimer) {
      window.clearTimeout(restoreTimer);
      restoreTimer = null;
    }
    heldKeys = keys.slice();
    var delay = holdFor(keys);
    if (delay > 0) restoreTimer = window.setTimeout(release, delay);
    else heldKeys = [];
  }

  /* Which delay to hold after a surface closes: the most important one that was
     actually up. A returning visitor whose cookie choice is already stored never
     went through 'consent: open', so they never wait. */
  function holdFor(keys) {
    var worst = null;
    for (var i = 0; i < keys.length; i++) {
      var surface = null;
      for (var s = 0; s < SURFACES.length; s++) {
        if (SURFACES[s].key === keys[i]) surface = SURFACES[s];
      }
      var delay = (surface && RESTORE_DELAY[surface.delay]) || 0;
      if (worst === null || delay > worst) worst = delay;
    }
    return worst || 0;
  }

  /* Read and publish, synchronously. A MutationObserver already coalesces a
     burst of changes into one callback, which is all the batching there was to
     do — and deferring the read to an animation frame or a timer means a hidden
     or throttled tab can leave the pill in the wrong state for seconds, which is
     exactly what a shopper sees when they come back to the tab. The signature
     comparison below is what keeps the attribute we write from re-entering. */
  function refresh() {
    readSurfaces();
    var now = blockingKeys();
    var sig = signature(now);
    if (sig === lastSignature) return;
    lastSignature = sig;

    var wasUp = liveKeys;
    liveKeys = now.slice();

    /* It owns the screen: hold the restore back and remember who. */
    if (now.length) {
      if (restoreTimer) {
        window.clearTimeout(restoreTimer);
        restoreTimer = null;
      }
      heldKeys = now.slice();
      publish();
      return;
    }

    /* Nothing is up, but something was a moment ago: that is the closing the
       CTA waits out. The animation is already over by the time we look, so
       "what was up at the previous read" is the only measure of it we have. */
    var waited = [];
    for (var i = 0; i < wasUp.length; i++) {
      /* Consent is the one exception, and only when it was never actually on
         screen: before the verdict arrives we report consent as blocking to
         stop the CTA flashing up behind a banner. A returning shopper whose
         choice is already stored never saw a banner, so they are owed no
         delay — only a banner that really closed earns the hold. */
      if (wasUp[i] === 'consent' && !consent.bannerSeen && !consent.collected) continue;
      waited.push(wasUp[i]);
    }
    if (waited.length) {
      arm(waited);
      publish();
      return;
    }

    if (restoreTimer) {
      publish();
      return;
    }
    heldKeys = [];
    publish();
  }

  /* ------------------------------------------------------------ public API */

  function whenClear(callback, delay) {
    var want = typeof delay === 'number' ? delay : 0;
    var fired = false;
    var attempt = function (detail) {
      if (fired) return;
      if (detail.blocking) return;
      fired = true;
      unsubscribe();
      if (want > 0) window.setTimeout(callback, want);
      else callback();
    };
    var unsubscribe = subscribe(attempt);
    /* In case the state is already clear and nothing is about to change. */
    if (!publishedBlocked) attempt({ blocking: false });
  }

  function subscribe(fn) {
    subscribers.push(fn);
    return function () {
      var at = subscribers.indexOf(fn);
      if (at !== -1) subscribers.splice(at, 1);
    };
  }

  window.RunItOverlays = {
    allowed: allowed,
    pageType: pageType,
    subscribe: subscribe,
    whenClear: whenClear,
    /* Live surfaces, for code that wants to know what is literally up… */
    blockingKeys: blockingKeys,
    /* …and the verdict consumers should gate on, which also covers a restore
       that is deliberately being held back after a surface closed. */
    isBlocking: function () { return publishedBlocked; },
    heldKeys: function () { return heldKeys.slice(); },
    /* What the last read saw, per surface. Read-only, and the thing to look at
       when a `painted()` judgement is not what the DOM appears to say. */
    surfaceState: function () {
      var out = { consent: state.consent };
      for (var i = 0; i < SURFACES.length; i++) {
        if (SURFACES[i].key !== 'consent') out[SURFACES[i].key] = state[SURFACES[i].key];
      }
      return out;
    },
    /* For other theme code that wants to announce a surface of its own. */
    refresh: refresh
  };

  /* One observer, early-exiting when nothing the coordinator cares about has
     changed. Attribute-filtered so the cart's own churn (aria-pressed on
     hearts, prices, quantities) cannot spin it. */
  var observer = new MutationObserver(refresh);
  observer.observe(document.documentElement, {
    attributes: true,
    subtree: true,
    attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden']
  });

  /* This theme opens and closes its drawers with a class plus a CSS transition,
     and `visibility` flipping at the end of that transition is not an attribute
     mutation — so the observer alone can read a drawer while it is still closing
     and never be told to look again. Two extra moments to re-read close that
     gap without polling: a press, and the end of a transition on a surface. */
  var WATCH = SURFACES.map(function (s) { return s.sels.join(','); }).join(',');

  /* A drawer here becomes visible a frame or two AFTER the class lands, and that
     flip is not an attribute change, so a single read on the press is too early
     and nothing tells us to look again. Three bounded re-reads of a few hundred
     milliseconds — not a poll, and every one of them is a no-op when nothing
     moved. Kept to one settle window at a time. */
  var settling = 0;

  function settle() {
    if (settling) return;
    settling = 3;
    [120, 320, 640].forEach(function (ms) {
      window.setTimeout(function () {
        settling--;
        refresh();
      }, ms);
    });
  }

  function afterInteraction() {
    refresh();
    settle();
  }

  document.addEventListener('click', afterInteraction);
  document.addEventListener('keyup', afterInteraction);
  document.addEventListener('transitionend', function (event) {
    var node = event.target;
    if (!node || !node.closest) return;
    if (node.closest(WATCH)) refresh();
  }, true);

  document.addEventListener('shopify:section:load', refresh);
  window.addEventListener('resize', refresh);

  bindConsentApi();
  readSurfaces();
  liveKeys = blockingKeys();
  lastSignature = signature(liveKeys);
  publish();

  /* A surface can also be announced explicitly, which is cheaper and more
     precise than waiting for the observer. */
  ['runit:cookie-open', 'runit:consent-ready', 'runit:wishlist-open']
    .forEach(function (name) { document.addEventListener(name, refresh); });
  document.addEventListener('runit:cookie-close', function () { hold(['consent']); });
  document.addEventListener('runit:wishlist-close', function () { hold(['wishlist']); });

  /* Announced last, so a consumer that was evaluated before this file could be
     (a section reload can re-inject either script first) can still bind. */
  document.dispatchEvent(new CustomEvent('runit:overlays-ready'));
})();
